import React, { useState } from 'react';
import { 
  TrendingUp, Users, Shield, Activity,
  FileText, Calendar, Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import StatCard from '../../components/molecules/StatCard';
import DashboardLayout from '../../components/layout/DashboardLayout';
import dayjs from 'dayjs';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface User {
  name: string;
  email: string;
  role: string;
  company: string;
}

const InsuranceDashboardOverview: React.FC = () => {
  const [user] = useState<User>({
    name: 'Mansour Salem',
    email: 'mansour.s@golden-falcon.ae',
    role: 'Administrator',
    company: 'Golden Falcon Insurance'
  });

  // Mock data for KPI cards
  const kpiData = {
    totalClaims: 1247,
    claimsThisMonth: 10,
    processedToday: 89,
    completedToday: 67,
    pendingReview: 156,
    automationRate: 87,
    fraudAlertsThisWeek: 8,
    savedAmount: 245000
  };

  // Chart data
  const chartData = {
    labels: ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Total Claims',
        data: [920, 1050, 1180, 1247, 1350, 1420],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
        borderRadius: 6,
      },
      {
        label: 'Approved Claims',
        data: [890, 1020, 1150, 1210, 1310, 1380],
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 2,
        borderRadius: 6,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        cornerRadius: 8,
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6B7280', font: { size: 12 } }
      },
      y: {
        grid: { color: '#F3F4F6', borderDash: [5, 5] },
        ticks: { color: '#6B7280', font: { size: 12 } }
      }
    }
  };

  const recentAlerts = [
    { id: 1, type: 'High Risk', provider: 'Dr. Ahmed Clinic', amount: 'AED 25,000', time: '2 hours ago' },
    { id: 2, type: 'Upcoding', provider: 'Al Noor Hospital', amount: 'AED 18,500', time: '5 hours ago' },
    { id: 3, type: 'Pattern Alert', provider: 'City Pharmacy', amount: 'AED 12,300', time: '1 day ago' },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Welcome Section */}
        <div>
            <h2 className="text-3xl font-bold text-gray-900">
              Welcome back, {user.name.split(' ')[0]}!
            </h2>
            <p className="text-gray-600 mt-1">
              {dayjs().format('dddd, MMMM D, YYYY')} • Here's your insurance operations overview
            </p>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <StatCard
              title="Total Claims"
              value={kpiData.totalClaims}
              icon={FileText}
              iconBgColor="bg-blue-100"
              iconColor="text-blue-600"
              trend={{
                value: kpiData.claimsThisMonth,
                isPositive: true,
                label: 'This month'
              }}
            />
            
            <StatCard
              title="Processed Today"
              value={kpiData.processedToday}
              icon={Calendar}
              iconBgColor="bg-green-100"
              iconColor="text-green-600"
              trend={{
                value: kpiData.completedToday,
                isPositive: true,
                label: `Completed: ${kpiData.completedToday}`
              }}
            />

            <StatCard
              title="Pending Review"
              value={kpiData.pendingReview}
              icon={Clock}
              iconBgColor="bg-purple-100"
              iconColor="text-purple-600"
              subtitle="Requires attention"
            />

            <StatCard
              title="Automation Rate"
              value={`${kpiData.automationRate}%`}
              icon={Activity}
              iconBgColor="bg-green-100"
              iconColor="text-green-600"
              trend={{
                value: 3,
                isPositive: true,
                label: 'vs last month'
              }}
            />

            <StatCard
              title="Fraud Alerts"
              value={kpiData.fraudAlertsThisWeek}
              icon={Shield}
              iconBgColor="bg-red-100"
              iconColor="text-red-600"
              subtitle="This week"
            />
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Alerts */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Recent Fraud Alerts</h3>
              
              <div className="space-y-4">
                {recentAlerts.map((alert) => (
                  <div key={alert.id} className="border border-gray-200 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                            alert.type === 'High Risk' ? 'bg-red-100 text-red-700' :
                            alert.type === 'Upcoding' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {alert.type}
                          </span>
                          <span className="font-semibold text-gray-900">{alert.provider}</span>
                        </div>
                        <p className="text-sm text-gray-600">Amount at risk: {alert.amount}</p>
                        <p className="text-xs text-gray-500 mt-1">{alert.time}</p>
                      </div>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors">
                        Review
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button className="w-full mt-4 py-3 border-2 border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors">
                View All Alerts
              </button>
            </div>

            {/* Chart */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Claims Overview</h3>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Total</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-xs text-gray-600">Approved</span>
                </div>
              </div>

              <div className="h-72">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Link
                to="/dashboard/user-management"
                className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Manage Users</p>
                  <p className="text-sm text-gray-600">Roles & permissions</p>
                </div>
              </Link>

              <Link
                to="/dashboard/fraud-detection"
                className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl hover:border-red-500 hover:bg-red-50 transition-all"
              >
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Fraud Detection</p>
                  <p className="text-sm text-gray-600">FWA analysis</p>
                </div>
              </Link>

              <Link
                to="/dashboard/risk-analytics"
                className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Risk Analytics</p>
                  <p className="text-sm text-gray-600">Proactive insights</p>
                </div>
              </Link>

              <Link
                to="/dashboard/claims"
                className="flex items-center gap-3 p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all"
              >
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">View Claims</p>
                  <p className="text-sm text-gray-600">All submissions</p>
                </div>
              </Link>
            </div>
          </div>
      </div>
    </DashboardLayout>
  );
};

export default InsuranceDashboardOverview;
